<template>
    <div class="reports">
        <div class="page-header">
            <div class="left">
                <h2 class="page-title">
                    <inertia-link :href="'/backoffice/reports'" class="name">
                        <v-icon color="#921E61" class="mb-1">mdi-arrow-left-circle</v-icon>
                        {{ $page.props.lang.back || "Reports" }}
                    </inertia-link>
                / {{ $page.props.lang.earnings || "Income" }}</h2>
            </div>
        </div>
        
        <div class="table-header">
            <div class="pickers">
                <div class="field-group">
                    <label class="custom-label">{{ $page.props.lang.fromDate || 'From date' }}</label>
                    <v-menu
                    ref="menu"
                    v-model="fromDatePicker"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="fromDate | formatDate"
                            append-icon="mdi-menu-down" outlined dense hide-details readonly v-bind="attrs" v-on="on" @click:append="fromDatePicker = true"></v-text-field>
                        </template>
                        <v-date-picker
                        v-model="fromDate" :max="toDate"
                        :first-day-of-week="1" no-title scrollable color="primary">
                            <v-spacer></v-spacer>
                            <v-btn text color="primary" @click="fromDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn text color="primary" @click="fromDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                        </v-date-picker>
                    </v-menu>
                </div>
                <div class="field-group">
                    <label class="custom-label">{{ $page.props.lang.toDate || 'To date' }}</label>
                    <v-menu
                    ref="menu"
                    v-model="toDatePicker"
                    :close-on-content-click="false"
                    :return-value.sync="date"
                    transition="scale-transition"
                    offset-y
                    min-width="290px">
                        <template v-slot:activator="{ on, attrs }">
                            <v-text-field :value="toDate | formatDate"
                            append-icon="mdi-menu-down" outlined dense hide-details readonly v-bind="attrs" v-on="on" @click:append="toDatePicker = true"></v-text-field>
                        </template>
                        <v-date-picker
                        v-model="toDate" :min="fromDate"
                        :first-day-of-week="1" locale="en" no-title scrollable color="primary">
                            <v-spacer></v-spacer>
                            <v-btn text color="primary" @click="toDatePicker = false">{{ $page.props.lang.cancel || "Cancel" }}</v-btn>
                            <v-btn text color="primary" @click="toDatePicker = false">{{ $page.props.lang.ok || "Ok" }}</v-btn>
                        </v-date-picker>
                    </v-menu>
                </div>
                <div class="field-group">
                    <label class="custom-label">{{ $page.props.lang.group_by || 'Group by' }}</label>
                    <v-select
                        v-model="periodValue" :items="periods" item-value="value" item-text="text" style="max-width:120px" dense outlined hide-details>
                    </v-select>
                </div>
                    <v-btn @click="filterByDates" color="primary" height="38">{{ $page.props.lang.apply || 'Apply' }}</v-btn>
            </div>  
        </div>
        <v-card elevation="1" class="mt-3" v-if="$page.props.p['members.payments.read']">
            <v-card-title>{{ $page.props.lang.earnings || "Income" }}</v-card-title>
            <v-card-text>
                <vue-frappe v-if="renderChart"
                    id="test"
                    :labels="chartLabels"
                    :height="300"
                    :line-options="{regionFill: 1}"
                    :axis-options="{xIsSeries: 1}"
                    :tooltip-options="tooltipOptions"
                    :colors="['#8b1f62']"
                    :dataSets="this.chartData">
                </vue-frappe>
            </v-card-text>
        </v-card>
        <div class="export-btn mt-4">
            <v-tooltip bottom open-delay="400">
                <template v-slot:activator="{on, attrs}">
                    <v-btn @click="exportIncomes" color="primary" fab x-small outlined v-bind="attrs" v-on="on">
                        <v-icon>mdi-file-excel-box</v-icon>
                    </v-btn>
                </template>
                <span>{{ $page.props.lang.export || 'Export' }}</span>
            </v-tooltip>
        </div>
        <div class="table-container mt-4">
            <v-data-table
                :headers="incomeHeaders"
                :items="data.results"
                :page.sync="tablePage"
                :items-per-page="itemsPerPage"
                @page-count="tablePageCount = $event"
                item-key="item.key"
                mobile-breakpoint="0"
                :footer-props="{
                    'items-per-page-options': [10, 30, 50, 100, 200, 500]
                }">
                <template v-slot:item.order="{ item }">
                    {{ item.key }}
                </template>
                <template v-slot:item.amount="{ item }">
                    {{ item.amount }} {{ $page.props.organization.settings.currency}}
                </template>
                <template v-slot:body.prepend="{item}">
                    <tr>
                        <td class="text-start">
                           <b> Total:</b>
                        </td>
                        <td class="text-right">
                            <b>{{data.total}} {{ $page.props.organization.settings.currency}}</b>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </div>
        <!-- <v-pagination v-model="tablePage" :length="tablePageCount" class="front-paginate"></v-pagination> -->
    </div>
</template>

<script>
import AppLayout from '@/js/Layouts/AppLayout'


export default {
    metaInfo() {
        return {
            title:  this.$page.props.lang.title || "Income Report"
        }
    },
    layout: AppLayout,
    props: {
        data: Object,
        lang: Object,
        dateFrom: String,
        dateTo: String,
        period_value: Number
    },
    data(){
        return {
            date: moment().format('YYYY-MM-DD'),
            fromDatePicker: false,
            fromDate: moment(this.dateFrom).format('YYYY-MM-DD'),
            toDatePicker: false,
            toDate: moment(this.dateTo).format('YYYY-MM-DD'),
            renderChart: false,
            tablePageCount: 0,
            tablePage: 1,
            tooltipOptions: {
                formatTooltipX: (d) => (d + ''),
                formatTooltipY: (d) => d + ' ' + this.$page.props.organization.settings.currency
            },
            incomeHeaders: [
                {text: this.getHeader(), value: 'order', align: 'start'},
                {text: this.lang.amount || 'Amount', value: 'amount', align: 'right'}
            ],
            periods:[
                {'text': this.lang.day || 'Day' , value: 0},
                {'text': this.lang.week || "Week", value: 1},
                {'text': this.lang.month || "Month", value: 2},
                {'text': this.lang.membership || "Membership", value: 3}
            ],
            periodValue: this.period_value,
            tablePage: 1,
            tablePageCount: 0,
            itemsPerPage: 10
               
        }
    },

    mounted() {
        this.renderChart = true
        
    },
    created(){
      //  this.$vuetify.lang.current = 'srLatn'
    },

    computed: {
        chartLabels() {
            return this.data.results.map(item => {
                return item.label
            })
        },
        chartData() {
            let values = this.data.results.map(item => {
                return item.amount
            })

            return [{
                name: "", 
                chartType: 'bar',
                values: values
            }]
        }
        
    },
    methods: {
        getHeader(){
         
           if(this.period_value === 0){
               return this.lang.day || "Days"
           }else if(this.period_value === 1){
               return this.lang.week || "Weeks"
           }else if(this.period_value === 2){
                return this.lang.month || "Months"
           }else{
               return this.lang.year || "Years"
           }
        },
        filterByDates() {
            this.$inertia.visit(
                '/backoffice/reports/income?dateFrom=' + this.fromDate + '&dateTo=' + this.toDate + '&period=' + this.periodValue, 
                {only:['data', 'dateFrom', 'dateTo', 'period_value']}
            )
        },
        exportIncomes(){
            console.log(this.periodValue);
             window.location.href = '/backoffice/reports/income/export?dateFrom=' + this.fromDate + '&dateTo=' + this.toDate + '&period=' + this.periodValue;
        }
    }
}

</script>